import { useCallback, useEffect, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { createSelector } from "reselect";

import TableContainer from "Common/Tables/StringsFiltersTable/TableContainer";
import ActionButton from "components/Buttons/ActionButton";
import { EditButton } from "components/Buttons/EditButton";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import StatusToggle from "components/StatusToggle";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import { toast } from "react-toastify";
import {
  fetchOrganisations as OnfetchOrganisations,
  updateStatusInOrg as onUpdateStatusInOrg,
  OrganisationsExportsCSV,
} from "slices/organisations/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";

const OrganisationsList = () => {
  document.title = "Organisation List";
  const dispatch = useDispatch<any>();
  const location = useLocation();

  // Use the preserved table state hook
  const tableState = usePreservedTableState({
    key: "organisation",
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue: colValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue: setColValue,
    resetState,
  } = tableState;

  const selectorganisation = createSelector(
    (state: any) => state.Organisations,
    (Org) => ({
      organisation: Org.organisationsList,
      rowCount: Org.rowCount,
      loading: Org.loading,
      error: Org.error,
    })
  );

  const { organisation, rowCount, loading, error } =
    useSelector(selectorganisation);

  // Use custom hook to fetch and access permissions
  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.user_management?.app_name,
    app_modules?.user_management?.modules?.organisation
  );

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  // Convert filterValue (filterField/filterValue) to ColumnFiltersState (id/value) format
  const convertedFilters = useMemo(() => {
    if (!colValue || !Array.isArray(colValue)) return [];
    return colValue.map((f: any) => ({
      id: f.filterField || f.id,
      value: f.filterValue !== undefined ? f.filterValue : f.value,
    }));
  }, [colValue]);

  useEffect(() => {
    dispatch(
      OnfetchOrganisations({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: convertedFilters,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    convertedFilters,
  ]);

  // Debounce search input to actual search
  useEffect(() => {
    // Skip if searchInput already matches search (prevents unnecessary updates during restoration)
    if (searchInput === search) {
      return;
    }

    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch, search]);

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  // Handle status toggle
  const handleStatusToggle = useCallback(async (org: any) => {
    // Prevent toggling status if sr_no is 1
    if (org.sr_no === 1) {
      toast.error("Cannot toggle status for this organisation.");
      return;
    }

    if (!canUpdate) {
      toast.error("You don't have Update permission.");
      return;
    }

    const updatedStatus = org.is_active === true ? false : true;
    const update_payload = { ...org, is_active: updatedStatus };

    try {
      await dispatch(onUpdateStatusInOrg(update_payload)).unwrap();

      toast.success(
        `Organisation ${
          updatedStatus ? "activated" : "deactivated"
        } successfully`
      );

      // Refresh the list
      dispatch(
        OnfetchOrganisations({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: convertedFilters,
        })
      );
    } catch (err: any) {
      // Extract error message from API response
      const errorMessage =
        err?.message ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to update organisation status";

      toast.error(errorMessage);
      console.error("Status update error:", err);
    }
  }, [canUpdate, dispatch, pageIndex, pageSize, sortField, sortDirection, search, convertedFilters]);

  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const { blob, filename } = await OrganisationsExportsCSV({
        search,
        filters: convertedFilters,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export completed successfully.",
      });
    } catch (error: any) {
      console.error("Export failed", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to export CSV";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    }
  };

  const DynamicAction = useMemo(() => {
    if (!canUpdate) return [];

    return [
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <EditButton
            to={`/edit-organisation/${row.original.organization_id}`}
          />
        ),
      },
    ];
  }, [canUpdate]);

  // Table columns
  const columns = useMemo(
    () => [
      {
        header: "Sr. No.",
        id: "sr_no", // important: since there's no accessorKey
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: any }) => {
          return row.original.sr_no || row.index + 1 + pageIndex * pageSize;
        },
      },
      {
        header: "Organisation",
        accessorKey: "organization_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "organization/dropdown/organization_name",
      },
      {
        header: "Address",
        accessorKey: "org_address",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "textarea" as const,
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              width: "250px",
            }}
          >
            {row.original.org_address}
          </div>
        ),
      },

      {
        header: "Industry",
        accessorKey: "industry_type",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "organization/dropdown/industry_type/",
      },
      {
        header: "City",
        accessorKey: "org_city",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "organization/dropdown/org_city",
      },
      {
        header: "State",
        accessorKey: "org_state",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "organization/dropdown/org_state",
      },
      {
        header: "Country",
        accessorKey: "org_country",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "organization/dropdown/org_country",
      },
      {
        header: "Pincode",
        accessorKey: "org_pincode",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "organization/dropdown/org_pincode",
      },
      {
        header: "Status",
        accessorKey: "is_active",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const org = row.original;
          return (
            <StatusToggle
              isActive={org.is_active}
              onChange={() => {
                handleStatusToggle(org);
              }}
              disabled={!canUpdate || org.sr_no === 1}
            />
          );
        },
      },

      //*// Grouped Column
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user?.username ?? "N/A";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "N/A";
          return `${name} - ${date}`;
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user?.username ?? "N/A";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "N/A";
          return `${name} - ${date}`;
        },
      },
      ...DynamicAction,
    ],
    [handleStatusToggle, canUpdate, DynamicAction, pageIndex, pageSize]
  );

  return (
    <ListingPageContainer>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Organisation</h6>
                </Col>

                {/* Search Input Box (reusable)*/}
                <Col xl={2} md={3}>
                  <SearchBox value={searchInput} onChange={setSearchInput} />
                </Col>

                <Col className="col-md-auto">
                  <div className="hstack gap-2">
                    {canView && (
                      <ActionButton
                        label="Export"
                        icon="bi bi-box-arrow-up"
                        handleClick={handleExport}
                        variant="light"
                      />
                    )}

                    {canCreate && (
                      <ActionButton
                        label="Add Organisation"
                        icon="bi bi-plus-circle"
                        navigateLink="/organisation-form"
                        variant="primary"
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper loading={loading} error={error}>
                <TableContainer
                  columns={columns}
                  data={organisation}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  colValue={setColValue}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  manualSorting={true}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  SearchPlaceholder="Search Organisations..."
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default OrganisationsList;
